import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { loadArtworkTexture } from '../artwork-texture.js';

// Shared-rig contract (see the framing addendum). Same shape as KaleidoDrift:
// one full-frame quad, one fragment shader, no 3D scene to light.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'drostedive',
  family: 'shader',
  energyLevel: 0.65,
  bpmMin: 100,
  bpmMax: 165,
  movement: 0.75,
  danceability: 0.50,
  tonality: 0.55,
  enabled: true
};

// DrosteDive: an infinite spiral zoom into the album cover, in the family of
// the Droste effect / Escher prints / VJ "falling into the image" loops.
//
// A real Droste recursion needs the source image to already contain a
// shrunken copy of itself, which no album cover does. This fakes it by
// sampling TWO consecutive logarithmic shells of the SAME image and
// crossfading between them as they cycle - the seam is where one shell
// finishes shrinking and the other has just spawned at full size, and the
// crossfade is timed so it happens while both are close in scale, which is
// what makes it invisible. Works on any image, no special content required.
//
// PERFORMANCE SHAPE - same family as KaleidoDrift: no geometry to speak of
// (two triangles), 100% fill-rate. Budget is TWO texture reads and ~40 ALU
// ops per pixel, mediump throughout, no loop, no procedural noise.

const CAMERA_POS = [0, 2, 8];
const CAMERA_LOOK = [0, 2, 0];

// log(ratio) between consecutive shells. log(3) is the spec's own default -
// a middle ground between a tight, fast-cycling recursion (log(2.2)) and a
// loose, slow one (log(4.5)).
const K_DEFAULT = Math.log(3.0);
const K_CHOICES = [Math.log(2.2), Math.log(3.0), Math.log(4.5)];

// Phrase length in beats - same convention as kaleido-drift.js's own
// PHRASE_BEATS and main-stage.js's WALL_PHRASE_BEATS (4 bars of 4/4). The
// recursion ratio only ever changes on a phrase downbeat, never mid-bar.
const PHRASE_BEATS = 16;
const PHRASE_SECONDS = 14; // fallback pacing when the track has no BPM
// Rare on purpose - "le geste le plus fort" reads as a deliberate choice
// only if it doesn't happen every phrase.
const REVERSE_PROBABILITY = 0.25;

// One full shell traversed every 8 beats, i.e. twice per 16-beat phrase - the
// zoom cycle loops on the same structure the recursion ratio changes on.
const SHELL_BEATS = 8;
const REFERENCE_BPM = 120;

// Maps shell-local radius (order 1, spanning ~[1/K_MAX_RATIO, K_MAX_RATIO))
// into a sane UV window on the artwork texture. Chosen empirically (see the
// verification pass in the file history) so the cover reads as a coherent
// image inside the iris rather than a mush of over-tiled repeats.
const TILE = 0.35;

const TWIST_MAX = 0.40;
const TWIST_PULSE_AMOUNT = 0.15;
const TWIST_PULSE_DECAY = 10;

const VIGNETTE_IN = 0.65;
const VIGNETTE_OUT = 1.10;
const VIGNETTE_DEPTH = 0.5;

// Reprised verbatim from kaleido-drift.js's own color-treatment block: a raw
// sampled cover renders brown, this is the fix.
const SATURATION_BOOST = 1.7;
const THRESHOLD_LO = 0.12;
const THRESHOLD_HI = 0.85;
const TINT_CYCLE_SPEED = 0.06;

const FALLBACK_COLORS = [0xff2fb0, 0x2fd8ff, 0xffb02f];
const FALLBACK_TEXTURE_SIZE = 64;

const TIER_LAYOUT = {
  // Two shells are mandatory at every tier - a single shell is a visible
  // seam, not an optional quality knob. Tier 0 drops the soft threshold and
  // the vignette instead.
  0: { saturate: 'simple', threshold: false, vignette: false },
  1: { saturate: 'simple', threshold: false, vignette: true },
  2: { saturate: 'full', threshold: true, vignette: true }
};
const DEFAULT_TIER = 1;

// Same clip-space trick as kaleido-drift.js: ignores the camera entirely,
// immune to SceneManager's Ken Burns drift.
const vertexShader = `
  precision mediump float;

  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

function buildFragmentShader(layout) {
  const saturate = layout.saturate === 'full'
    ? `
    float lum = dot(col, vec3(0.299, 0.587, 0.114));
    col = mix(vec3(lum), col, ${SATURATION_BOOST.toFixed(2)});
    `
    : layout.saturate === 'simple'
    ? `
    // Cheaper stand-in for the full perceptual luminance dot-product below -
    // still pushes the sample away from a per-pixel grey, just with a single
    // channel read instead of three.
    float lum = col.g;
    col = mix(vec3(lum), col, ${SATURATION_BOOST.toFixed(2)});
    `
    : '';

  const threshold = layout.threshold ? `
    col = smoothstep(${THRESHOLD_LO.toFixed(2)}, ${THRESHOLD_HI.toFixed(2)}, col);
  ` : '';

  const vignette = layout.vignette ? `
    col *= 1.0 - smoothstep(${VIGNETTE_IN.toFixed(2)}, ${VIGNETTE_OUT.toFixed(2)}, length(p)) * ${VIGNETTE_DEPTH.toFixed(2)};
  ` : '';

  return `
  precision mediump float;

  uniform sampler2D uArtwork;
  uniform float uAspect;
  uniform vec2 uCenter;
  uniform float uTwist;
  uniform float uK;
  uniform float uTime;
  uniform float uSpeed;
  uniform float uTile;
  uniform float uTintPhase;
  uniform float uDebug;
  uniform vec3 uVivid0;
  uniform vec3 uVivid1;
  uniform vec3 uVivid2;

  varying vec2 vUv;

  // Same three-stop cyclic mix as kaleido-drift.js's own paletteTint, minus
  // the fold-angle term - there is no mirror fold here, so the tint simply
  // rotates through the palette over time instead of over the folded angle.
  vec3 paletteTint(float phase) {
    float ti = mod(phase, 3.0);
    if (ti < 1.0) return mix(uVivid0, uVivid1, ti);
    if (ti < 2.0) return mix(uVivid1, uVivid2, ti - 1.0);
    return mix(uVivid2, uVivid0, ti - 2.0);
  }

  void main() {
    vec2 p = (vUv - 0.5) * vec2(uAspect, 1.0) - uCenter;
    float r = max(length(p), 1e-4);
    float a = atan(p.y, p.x);

    float lr = log(r);
    a += lr * uTwist;
    float t = lr / uK - uTime * uSpeed;

    float f = fract(t);

    // Debug: render the raw shell-progress value in place of color. Regular,
    // concentric, seamless rings flowing toward the center is the only
    // acceptable result - see the constructor's own comment for what this is
    // checking and why it has to pass before anything else does.
    if (uDebug > 0.5) {
      gl_FragColor = vec4(vec3(f), 1.0);
      return;
    }

    float r0 = exp(f * uK);
    float r1 = exp((f - 1.0) * uK);
    vec2 dir = vec2(cos(a), sin(a));
    vec2 q0 = dir * r0;
    vec2 q1 = dir * r1;

    vec3 c0 = texture2D(uArtwork, fract(q0 * uTile + 0.5)).rgb;
    vec3 c1 = texture2D(uArtwork, fract(q1 * uTile + 0.5)).rgb;
    vec3 col = mix(c0, c1, smoothstep(0.05, 0.95, f));

    ${saturate}
    ${threshold}

    col *= paletteTint(uTintPhase);

    ${vignette}

    gl_FragColor = vec4(col, 1.0);
  }
  `;
}

// Same crossed-sine bake as kaleido-drift.js's own fallback, kept local
// rather than imported since it is a private implementation detail there -
// the shared surface between the two is loadArtworkTexture (artwork-texture.js)
// and the color-treatment GLSL block above, not this generator.
function buildFallbackTexture() {
  const N = FALLBACK_TEXTURE_SIZE;
  const data = new Uint8Array(N * N * 4);
  const c = FALLBACK_COLORS.map((hex) => new THREE.Color(hex));
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      const u = (x / N) * Math.PI * 2;
      const v = (y / N) * Math.PI * 2;
      const s0 = 0.5 + 0.5 * Math.sin(u * 3);
      const s1 = 0.5 + 0.5 * Math.sin(v * 2 + 1.7);
      const s2 = 0.5 + 0.5 * Math.sin((u + v) * 2 + 0.9);
      const i = (y * N + x) * 4;
      data[i] = Math.round(255 * Math.min(1, c[0].r * s0 + c[1].r * s1 + c[2].r * s2));
      data[i + 1] = Math.round(255 * Math.min(1, c[0].g * s0 + c[1].g * s1 + c[2].g * s2));
      data[i + 2] = Math.round(255 * Math.min(1, c[0].b * s0 + c[1].b * s1 + c[2].b * s2));
      data[i + 3] = 255;
    }
  }
  const texture = new THREE.DataTexture(data, N, N);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

export class DrosteDiveVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER_LAYOUT[tier] ? tier : DEFAULT_TIER;

    // Mandated verification step (see the brief): ?drostedebug=1 renders `f`
    // as grayscale instead of color, to check the log-polar repli itself
    // before anything built on top of it is trusted.
    const params = new URLSearchParams(typeof location !== 'undefined' ? location.search : '');
    this._debug = params.get('drostedebug') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;

    camera.position.set(CAMERA_POS[0], CAMERA_POS[1], CAMERA_POS[2]);
    camera.lookAt(CAMERA_LOOK[0], CAMERA_LOOK[1], CAMERA_LOOK[2]);

    this._fallbackTexture = buildFallbackTexture();

    this._uniforms = {
      uArtwork: { value: this._fallbackTexture },
      uAspect: { value: 16 / 9 },
      uCenter: { value: new THREE.Vector2(0, 0) },
      uTwist: { value: 0 },
      uK: { value: K_DEFAULT },
      uTime: { value: 0 },
      uSpeed: { value: REFERENCE_BPM / (60 * SHELL_BEATS) },
      uTile: { value: TILE },
      uTintPhase: { value: 0 },
      uDebug: { value: this._debug ? 1 : 0 },
      uVivid0: { value: new THREE.Color(FALLBACK_COLORS[0]) },
      uVivid1: { value: new THREE.Color(FALLBACK_COLORS[1]) },
      uVivid2: { value: new THREE.Color(FALLBACK_COLORS[2]) }
    };

    const layout = TIER_LAYOUT[this._tier];
    const geometry = new THREE.PlaneGeometry(1, 1);
    this._material = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: vertexShader,
      fragmentShader: buildFragmentShader(layout),
      transparent: false,
      depthTest: false,
      depthWrite: false
    });
    this._quad = new THREE.Mesh(geometry, this._material);
    this._quadGeometry = geometry;
    this._quad.frustumCulled = false;
    this._quad.renderOrder = -1000;
    scene.add(this._quad);

    this._time = 0;
    // +1 = diving in, -1 = climbing back out ("on remonte au lieu de
    // plonger"). Flips rarely, on a phrase boundary - see _switchPhrase.
    this._direction = 1;
    this._beatCount = 0;
    this._lastBeatPhase = null;
    this._phraseEndsAtBeat = PHRASE_BEATS;
    this._phraseEndsAtTime = PHRASE_SECONDS;
    this._lastArtworkUrl = null;
    this._lastPaletteRef = null;
    this._hasArtwork = false;
    this._artworkTexture = null;
  }

  _loadArtwork(url) {
    // RepeatWrapping is load-bearing here too - both shells wrap through
    // fract() every frame, and mipmaps matter more than for KaleidoDrift:
    // the outer reaches of each shell repeat the cover many times across the
    // frame, which is exactly the minification case mipmaps exist for.
    loadArtworkTexture(
      url,
      { wrap: THREE.RepeatWrapping, mipmaps: true },
      (texture) => {
        const previous = this._artworkTexture;
        this._artworkTexture = texture;
        this._uniforms.uArtwork.value = texture;
        this._hasArtwork = true;
        if (previous) previous.dispose();
      },
      undefined,
      () => {
        this._hasArtwork = false;
        this._uniforms.uArtwork.value = this._fallbackTexture;
      }
    );
  }

  _switchPhrase() {
    const current = this._uniforms.uK.value;
    const others = K_CHOICES.filter((k) => Math.abs(k - current) > 1e-6);
    this._uniforms.uK.value = others[Math.floor(Math.random() * others.length)];
    if (Math.random() < REVERSE_PROBABILITY) this._direction *= -1;
    this._phraseEndsAtBeat = this._beatCount + PHRASE_BEATS;
    this._phraseEndsAtTime = this._time + PHRASE_SECONDS;
  }

  _applyPalette(profile) {
    const palette = profile.artworkPalette;
    const vivid = (palette && palette.vividColors) || [];
    const slots = [this._uniforms.uVivid0.value, this._uniforms.uVivid1.value, this._uniforms.uVivid2.value];
    for (let i = 0; i < 3; i++) {
      slots[i].set(vivid.length > 0 ? vivid[i % vivid.length] : FALLBACK_COLORS[i]);
    }
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    // Freezing time freezes the dive itself - the spiral stops dead rather
    // than drifting on without the beat that drives it.
    if (!paused) this._time += deltaTime;
    this._uniforms.uTime.value = this._time;

    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || REFERENCE_BPM;
    const speedMagnitude = bpm / (60 * SHELL_BEATS);
    this._uniforms.uSpeed.value = speedMagnitude * this._direction;

    this._uniforms.uTintPhase.value = this._time * TINT_CYCLE_SPEED;

    if (profile.artworkUrl !== this._lastArtworkUrl) {
      this._lastArtworkUrl = profile.artworkUrl;
      this._hasArtwork = false;
      this._uniforms.uArtwork.value = this._fallbackTexture;
      if (profile.artworkUrl) this._loadArtwork(profile.artworkUrl);
    }
    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._applyPalette(profile);
    }

    // Recursion ratio (and, rarely, direction) changes only on a phrase
    // downbeat - never mid-bar, same discipline as kaleido-drift.js's own
    // segment switch.
    if (!paused && profile.beatPhase != null) {
      if (this._lastBeatPhase != null && profile.beatPhase < this._lastBeatPhase) this._beatCount++;
      this._lastBeatPhase = profile.beatPhase;
    } else {
      this._lastBeatPhase = null;
    }
    if (!paused) {
      const dueOnBeat = profile.trackFeatures && profile.trackFeatures.bpm
        && profile.beatPhase != null && this._beatCount >= this._phraseEndsAtBeat;
      const dueOnClock = !(profile.trackFeatures && profile.trackFeatures.bpm)
        && this._time >= this._phraseEndsAtTime;
      if (dueOnBeat || dueOnClock) this._switchPhrase();
    }

    // Base twist arcs 0 -> 0.40 -> 0 across the track (frontal zoom at the
    // edges, spiral in the middle), with a small additive pulse on the beat
    // so the spiral tightens rather than flashing.
    const intensity = profile.director.intensity;
    const pulse = profile.director.pulse(TWIST_PULSE_AMOUNT, TWIST_PULSE_DECAY) - 1;
    this._uniforms.uTwist.value = TWIST_MAX * intensity + pulse;

    this._uniforms.uAspect.value = this._camera.aspect;
  }

  dispose() {
    this._scene.remove(this._quad);
    this._quadGeometry.dispose();
    this._material.dispose();
    this._fallbackTexture.dispose();
    if (this._artworkTexture) {
      this._artworkTexture.dispose();
      this._artworkTexture = null;
    }
  }
}
